import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../shop/CartContext";
import { useCustomerAuth } from "../shop/AuthContext";
import ShopAuthCallback from "../shop/AuthCallback";
import { createOrder, createPayment, fetchShippingInfo } from "../shop/api";
import { StepIndicator, CartReview, SignInStep, ShippingStep, PaymentStep } from "../shop/CheckoutSteps";
import { useSEO } from "../lib/seo";

function submitToPayU(action, fields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function CheckoutPage() {
  useSEO({
    title: "Checkout — GenieX Shop",
    description: "Secure checkout for your GenieX smart home order.",
    path: "/checkout",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCart();
  const auth = useCustomerAuth();
  const [cartConfirmed, setCartConfirmed] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);

  useEffect(() => {
    fetchShippingInfo().then(setShippingInfo).catch(() => {});
  }, []);

  const hashHasSession = (location.hash || "").includes("session_id=");
  if (hashHasSession) {
    return <ShopAuthCallback />;
  }

  if (auth.loading) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] pt-40 text-center text-dim" data-testid="checkout-loading">
        Loading…
      </main>
    );
  }

  if (cart.items.length === 0) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] text-white pt-40 pb-24 text-center" data-testid="checkout-empty">
        <p className="text-dim">Your cart is empty.</p>
        <button onClick={() => navigate("/shop")} data-testid="checkout-empty-shop-link" className="btn-outline mt-6 mx-auto">
          Browse the Shop
        </button>
      </main>
    );
  }

  let step = "cart";
  if (cartConfirmed) {
    if (!auth.user) step = "signin";
    else if (!shippingAddress) step = "shipping";
    else step = "payment";
  }

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const order = await createOrder({
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.qty })),
        shipping_address: shippingAddress,
      });
      const payment = await createPayment(order.id);
      submitToPayU(payment.action, payment.fields);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPlacing(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="bg-[var(--gx-bg)] text-white pt-28 lg:pt-36 pb-24">
      <div className="max-w-[760px] mx-auto px-5 lg:px-0">
        <p className="eyebrow mb-3 text-center">Checkout</p>
        <StepIndicator current={step} />

        {step === "cart" && <CartReview cart={cart} shippingInfo={shippingInfo} onContinue={() => setCartConfirmed(true)} />}
        {step === "signin" && <SignInStep onLogin={auth.loginWithGoogle} />}
        {step === "shipping" && <ShippingStep initial={shippingAddress} onContinue={setShippingAddress} />}
        {step === "payment" && (
          <PaymentStep cart={cart} shippingAddress={shippingAddress} shippingInfo={shippingInfo} placing={placing} onPlaceOrder={placeOrder} />
        )}
      </div>
    </main>
  );
}
